import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import type { Notification } from './types';

interface UseNotificationsReturn {
  notifications: Notification[];
  allRead: boolean;
  markAsRead: (id: string) => Promise<void>;
  unreadCount: number;
  loading: boolean;
  refresh: () => Promise<void>;
}

export const useNotifications = (
  username?: string,
  pharmacyNum?: string | null,
  role?: string | null
): UseNotificationsReturn => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  const fetchNotifications = useCallback(async () => {
    const effectiveUsername = username || localStorage.getItem('username');
    const effectivePharmacyNum = pharmacyNum || localStorage.getItem('pharmacyNum');
    const effectiveRole = role || localStorage.getItem('role') || 'user';

    if (!effectiveUsername) {
      setNotifications([]);
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get('https://tbl.zerdegroup.kz/api/notifications/my', {
        params: {
          username: effectiveUsername,
          pharmacyNum: effectivePharmacyNum || '',
          role: effectiveRole,
        },
      });

      if (response.data.success && Array.isArray(response.data.notifications)) {
        setNotifications(response.data.notifications);
      }
    } catch (error) {
      console.error('Ошибка загрузки уведомлений:', error);
    } finally {
      setLoading(false);
    }
  }, [username, pharmacyNum, role]);

  useEffect(() => {
    fetchNotifications();

    // Опрос каждые 3 минуты для получения новых триггеров или уведомлений
    const interval = setInterval(fetchNotifications, 3 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  const markAsRead = useCallback(async (id: string) => {
    const effectiveUsername = username || localStorage.getItem('username');
    const effectivePharmacyNum = pharmacyNum || localStorage.getItem('pharmacyNum');

    // Оптимистичное обновление UI
    setNotifications((prev) =>
      prev.map((n) => (n.id === id ? { ...n, isRead: true } : n))
    );

    try {
      await axios.post(`https://tbl.zerdegroup.kz/api/notifications/${id}/read`, {
        username: effectiveUsername,
        pharmacyNum: effectivePharmacyNum || '',
      });
    } catch (error) {
      console.error('Ошибка отметки уведомления как прочитанного:', error);
    }
  }, [username, pharmacyNum]);

  const unreadCount = notifications.filter((n) => !n.isRead).length;
  const allRead = notifications.length > 0 ? unreadCount === 0 : true;

  return {
    notifications,
    allRead,
    markAsRead,
    unreadCount,
    loading,
    refresh: fetchNotifications,
  };
};

